if ( typeof OmnibusTooltip !== 'function' ) {
    class OmnibusTooltip extends HTMLElement {
        constructor() {
            super();

            this._isOpen = false;
            this._observer = null;

            this._refreshScheduled = false;
            this._lastText = "";

            this._portalNode = null;
            this._portalPlaceholder = null;
            this._portalOriginalParent = null;

            this._onMouseEnter = this._onMouseEnter.bind(this);
            this._onMouseLeave = this._onMouseLeave.bind(this);
            this._onFocus = this._onFocus.bind(this);
            this._onBlur = this._onBlur.bind(this);
            this._onClick = this._onClick.bind(this);
            this._onKeyDown = this._onKeyDown.bind(this);
            this._onMutations = this._onMutations.bind(this);
        }

        static get observedAttributes() {
            return [
            "fallback",
            "trigger-label",
            "scope-selector",
            "source-selector",
            "label-selector",
            "price-selector",
            "tooltip-id",
            "content-mode"
            ];
        }

        connectedCallback() {
            this._render();
            this._wire();
            this._refreshContent();
            this._observeDom();
        }

        disconnectedCallback() {
            this._unobserveDom();
            this._unwire();
        }

        attributeChangedCallback() {
            this._scheduleRefresh();
        }

        // -------------------------
        // Helpers
        // -------------------------

        static toBool(value) {
            return value === true || value === "true" || value === "1";
        }

        static safeTrim(text) {
            if (!text) return "";
            return String(text).trim();
        }

        static ensureId(id) {
            if (id && id.length > 0) return id;
            return `omnibus-tooltip-${Math.random().toString(36).slice(2)}`;
        }

        static escapeHtml(str) {
            return String(str)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");
        }

        static getReadableText(root) {
            if (!root) return "";

            const clone = root.cloneNode(true);
            const nodesToRemove = clone.querySelectorAll ? clone.querySelectorAll("script, style") : [];

            for (const node of nodesToRemove) node.remove();

            return OmnibusTooltip.safeTrim(clone.textContent);
        }

        static getTextFromSource(root, labelSelector, priceSelector) {
            if (!root) return "";

            const labelEl = root.querySelector(labelSelector);
            const priceEl = root.querySelector(priceSelector);

            const label = labelEl ? OmnibusTooltip.safeTrim(labelEl.textContent) : "";
            const price = priceEl ? OmnibusTooltip.safeTrim(priceEl.textContent) : "";

            if (label && price) return `${label} ${price}`;
            if (label) return label;
            if (price) return price;
            return "";
        }

        // -------------------------
        // Render / Wire
        // -------------------------

        _render() {
            if (this.querySelector(".omnibus-tooltip")) return;

            const tooltipId = OmnibusTooltip.ensureId(this.getAttribute("tooltip-id"));
            this.setAttribute("tooltip-id", tooltipId);

            const triggerLabel = this.getAttribute("trigger-label") || "Detalii preț";

            this.innerHTML =
            `<div class="omnibus-tooltip">
                <button
                type="button"
                class="omnibus-tooltip__trigger"
                aria-expanded="false"
                aria-describedby="${tooltipId}"
                >
                    <span class="visually-hidden">${OmnibusTooltip.escapeHtml(triggerLabel)}</span>
                    <svg style="fill: currentColor; width: 18px;height: 18px" version="1.1" id="Capa_1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 330 330" xml:space="preserve">
                    <g>
                        <path d="M165,0C74.019,0,0,74.02,0,165.001C0,255.982,74.019,330,165,330s165-74.018,165-164.999C330,74.02,255.981,0,165,0z M165,300c-74.44,0-135-60.56-135-134.999C30,90.562,90.56,30,165,30s135,60.562,135,135.001C300,239.44,239.439,300,165,300z"></path>
                        <path d="M164.998,70c-11.026,0-19.996,8.976-19.996,20.009c0,11.023,8.97,19.991,19.996,19.991 c11.026,0,19.996-8.968,19.996-19.991C184.994,78.976,176.024,70,164.998,70z"></path>
                        <path d="M165,140c-8.284,0-15,6.716-15,15v90c0,8.284,6.716,15,15,15c8.284,0,15-6.716,15-15v-90C180,146.716,173.284,140,165,140z"></path>
                    </g>
                    </svg>
                </button>
                <div id="${tooltipId}" class="omnibus-tooltip__content" role="tooltip" hidden></div>
            </div>`;
        }

        _wire() {
            const trigger = this._getTrigger();
            if (!trigger) return;

            trigger.addEventListener("mouseenter", this._onMouseEnter);
            this.addEventListener("mouseleave", this._onMouseLeave);
            trigger.addEventListener("focus", this._onFocus);
            trigger.addEventListener("blur", this._onBlur);
            trigger.addEventListener("click", this._onClick);

            // Use document from this element (safer in theme editors/iframes)
            this.ownerDocument.addEventListener("keydown", this._onKeyDown);
        }

        _unwire() {
            const trigger = this._getTrigger();
            if (!trigger) return;

            trigger.removeEventListener("mouseenter", this._onMouseEnter);
            this.removeEventListener("mouseleave", this._onMouseLeave);
            trigger.removeEventListener("focus", this._onFocus);
            trigger.removeEventListener("blur", this._onBlur);
            trigger.removeEventListener("click", this._onClick);

            this.ownerDocument.removeEventListener("keydown", this._onKeyDown);
        }

        // -------------------------
        // Scoped lookup
        // -------------------------

        _getScopeRoot() {
            const explicit = this.getAttribute("scope-selector");
            if (explicit) {
            const root = this.ownerDocument.querySelector(explicit);
            if (root) return root;
            }

            return (
            this.closest("product-page") ||
            this.closest("[data-js-product-component]") ||
            this.closest(".main-product") ||
            this.ownerDocument
            );
        }

        _findOmnibusRoot(scopeRoot) {
            const sourceSelector = this.getAttribute("source-selector") || "#omnibus-price";

            // 1) direct in scope
            const direct = scopeRoot.querySelector(sourceSelector);
            if (direct) return direct;

            // 2) inside sniffie app block
            const appBlock = scopeRoot.querySelector(
            '[data-shopify-editor-block*="sniffie_omnibus_pricing"]'
            );
            if (appBlock) {
            const inside = appBlock.querySelector(sourceSelector);
            if (inside) return inside;
            }

            return null;
        }

        _getContentMode() {
            return (this.getAttribute("content-mode") || "text").toLowerCase();
        }

        // -------------------------
        // MutationObserver (NO self-loop)
        // -------------------------

        _observeDom() {
            if (!window.MutationObserver) return;

            const scopeRoot = this._getScopeRoot();

            // Prefer observing the omnibus node (best: avoids self mutations)
            const omnibusRoot = scopeRoot === this.ownerDocument ? null : this._findOmnibusRoot(scopeRoot);
            const target = omnibusRoot || scopeRoot;

            this._observer = new MutationObserver(this._onMutations);

            // IMPORTANT:
            // - do NOT observe characterData (it gets very noisy)
            // - subtree needed because app may update inside the container
            this._observer.observe(target, {
            childList: true,
            subtree: true
            });
        }

        _onMutations(mutations) {
            // If we are observing a large scope, ignore mutations coming from inside this component
            // (prevents infinite feedback loop)
            for (let i = 0; i < mutations.length; i++) {
            const m = mutations[i];
            if (m && m.target && this.contains(m.target)) {
                if (this._getContentMode() === "portal" && this._portalNode && this._portalNode.contains(m.target)) {
                this._scheduleRefresh();
                return;
                }
                continue;
            }
            this._scheduleRefresh();
            return;
            }
        }

        _unobserveDom() {
            if (!this._observer) return;
            this._observer.disconnect();
            this._observer = null;
        }

        _scheduleRefresh() {
            if (this._refreshScheduled) return;
            this._refreshScheduled = true;

            // Throttle to once per frame
            requestAnimationFrame(() => {
            this._refreshScheduled = false;
            this._refreshContent();
            });
        }

        // -------------------------
        // Content logic
        // -------------------------

        _refreshContent() {
            const trigger = this._getTrigger();
            const content = this._getContent();
            if (!trigger || !content) return;

            if (this._getContentMode() === "portal") {
            const scopeRoot = this._getScopeRoot();
            const portalNode =
                scopeRoot === this.ownerDocument ? this.ownerDocument.querySelector("#omnibus-price") : this._findOmnibusRoot(scopeRoot);

            const readableText = OmnibusTooltip.getReadableText(portalNode);

            if (!readableText) {
                trigger.style.display = "none";
                this._unportalFromTooltip();
                this._close();
                return;
            }

            trigger.style.display = "";
            return;
            }

            const scopeRoot = this._getScopeRoot();

            const labelSelector = this.getAttribute("label-selector") || ".omnibus-price__label";
            const priceSelector = this.getAttribute("price-selector") || ".omnibus-price__price";
            const fallback = OmnibusTooltip.safeTrim(this.getAttribute("fallback"));

            const omnibusRoot =
            scopeRoot === this.ownerDocument ? this.ownerDocument.querySelector("#omnibus-price") : this._findOmnibusRoot(scopeRoot);

            let text = OmnibusTooltip.getTextFromSource(omnibusRoot, labelSelector, priceSelector);
            if (!text && fallback) text = fallback;

            if (!text) {
            trigger.style.display = "none";
            if (this._lastText !== "") {
                this._lastText = "";
                content.textContent = "";
            }
            this._close();
            return;
            }

            trigger.style.display = "";

            // Avoid writing the same text repeatedly (reduces mutations / reflow)
            if (text !== this._lastText) {
            this._lastText = text;
            content.textContent = text;
            }
        }

        _open() {
            const trigger = this._getTrigger();
            const content = this._getContent();
            if (!trigger || !content) return;

            this._refreshContent();

            if (this._getContentMode() === "portal") {
            const scopeRoot = this._getScopeRoot();
            const portalNode =
                scopeRoot === this.ownerDocument ? this.ownerDocument.querySelector("#omnibus-price") : this._findOmnibusRoot(scopeRoot);

            if (!portalNode) return;

            const readableText = OmnibusTooltip.getReadableText(portalNode);
            if (!readableText) return;

            this._portalToTooltip(portalNode);
            } else {
            if (!content.textContent) return;
            }

            content.hidden = false;
            trigger.setAttribute("aria-expanded", "true");
            this._isOpen = true;
        }

        _close() {
            const trigger = this._getTrigger();
            const content = this._getContent();
            if (!trigger || !content) return;

            if (this._getContentMode() === "portal") {
            this._unportalFromTooltip();
            }

            content.hidden = true;
            trigger.setAttribute("aria-expanded", "false");
            this._isOpen = false;
        }

        _toggle() {
            if (OmnibusTooltip.toBool(this._isOpen)) this._close();
            else this._open();
        }

        // -------------------------
        // Events
        // -------------------------

        _onMouseEnter() {
            this._open();
        }

        _onMouseLeave() {
            this._close();
        }

        _onFocus() {
            this._open();
        }

        _onBlur() {
            this._close();
        }

        _onClick(e) {
            e.preventDefault();
            this._toggle();
        }

        _onKeyDown(e) {
            if (e.key === "Escape") this._close();
        }

        // -------------------------
        // DOM getters
        // -------------------------

        _getTrigger() {
            return this.querySelector(".omnibus-tooltip__trigger");
        }

        _getContent() {
            return this.querySelector(".omnibus-tooltip__content");
        }

        // -------------------------
        // Portal mode (move real DOM)
        // -------------------------

        _portalToTooltip(node) {
            const content = this._getContent();
            if (!content || !node) return;

            if (this._portalNode && this._portalNode === node) {
            if (node.parentNode !== content) content.appendChild(node);
            return;
            }

            this._unportalFromTooltip();

            const parent = node.parentNode;
            if (!parent) return;

            this._portalNode = node;
            this._portalOriginalParent = parent;
            this._portalPlaceholder = this.ownerDocument.createComment("omnibus-price-placeholder");

            parent.insertBefore(this._portalPlaceholder, node);
            content.appendChild(node);
        }

        _unportalFromTooltip() {
            if (!this._portalNode) return;

            const node = this._portalNode;
            const placeholder = this._portalPlaceholder;
            const originalParent = this._portalOriginalParent;

            if (placeholder && placeholder.parentNode) {
            placeholder.parentNode.insertBefore(node, placeholder);
            placeholder.parentNode.removeChild(placeholder);
            } else if (originalParent) {
            originalParent.appendChild(node);
            }

            this._portalNode = null;
            this._portalPlaceholder = null;
            this._portalOriginalParent = null;
        }
    }

    if (!customElements.get("omnibus-tooltip")) {
    customElements.define("omnibus-tooltip", OmnibusTooltip);
    }
}